import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunnerOptionalParameter,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { ApiParameter, HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import { getShipTos, GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { setDataViewLoaded } from "@insite/client-framework/Store/Data/DataState";
import { currentShipTosApiParameter } from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { BillToModel, ShipToCollectionModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import {
    CustomApiHandler,
    CustomApiHandlerDiscreteParameter,
} from "@tLinc/Store/CustomHandlerCreator/CustomHandlerCreator";
import { ShipTosResultState } from "@tLinc/Store/Reducer/ShipTosInfoReducer";
import initialState from "@tLinc/Store/Reducer/ShipTosInfoReducer";

interface Filter {
    searchText: string;
}

type HandlerType = CustomApiHandlerDiscreteParameter<
    GetShipTosApiParameter & HasOnSuccess & Filter,
    GetShipTosApiParameter,
    ShipToCollectionModel
>;

export const DispatchBeginLoadShipTos: HandlerType = props => {
    props.dispatch({
        type: "Data/ShipTos/BeginLoadShipTos",
        result: props.parameter,
    });
};

export const PopulateApiParameter: HandlerType = props => {
    const newParameter = {
        ...props.parameter,
    };
    delete newParameter.onSuccess;
    props.apiParameter = newParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    const dataViews = props.getState().data.billTos.dataViews;
    const keys = Object.keys(dataViews);
    const billTosIds = dataViews[keys[keys.length - 1]] ? dataViews[keys[keys.length - 1]].ids : "";
    const shipTosData = await Promise.all(
        (billTosIds || []).map(async billTo => {
            const result = await getShipTos({
                expand: ["validation"],
                billToId: billTo,
                exclude: ["showAll", "oneTime", "createNew"],
                filter: props.parameter.searchText ?? "",
            });
            return result || { pagination: {}, properties: {}, shipTos: null, uri: "" };
        }),
    );

    if (!shipTosData.length) {
        // Handle the case where shipTosData is empty
        return;
    }

    const { pagination, properties } = shipTosData[0];

    const combinedObject = {
        pagination,
        properties,
        uri: "",
        shipTos: shipTosData.reduce((mergedShipTos, currentItem) => {
            if (currentItem.shipTos !== null) {
                mergedShipTos.push(...(currentItem.shipTos || []));
            }
            return mergedShipTos;
        }, [] as ShipToModel[]), // Initialize mergedShipTos as an empty array of ShipToModel
    };

    props.apiResult = combinedObject;

    props.dispatch({
        type: "Custom/ShipTos/CompleteLoadShipTos",
        result: combinedObject,
    });
};

export const DispatchCompleteLoadShipTos: HandlerType = props => {
    const modifiedParam = { ...props.getState().pages.addresses.getShipTosParameter };
    if (!props.apiResult) {
        return;
    }
    props.dispatch({
        type: "Data/ShipTos/CompleteLoadShipTos",
        parameter: props.parameter,
        collection: props.apiResult,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginLoadShipTos,
    PopulateApiParameter,
    RequestDataFromApi,
    DispatchCompleteLoadShipTos,
    ExecuteOnSuccessCallback,
];

const loadShipTos = createHandlerChainRunnerOptionalParameter(
    chain,
    {
        searchText: "",
    },
    "LoadShipTos",
);

export default loadShipTos;
